/**
 * The update check. R1 Step 15.
 *
 * Hard rule 1 in practice: this is the only outbound request the application
 * makes at runtime that is not the browser loading the app's own assets, so it
 * is the one place where user content could leak. Every request it builds is
 * put through {@link decideRequest} before `fetch` is reached, and a blocked
 * request is refused rather than sent.
 *
 * Note what is deliberately absent: there is no cache-busting query string.
 * A query string is the ordinary way a version check ends up carrying state,
 * so freshness is asked for with `cache: "no-store"` instead.
 */

import {
  VERSION_MANIFEST_PATH,
  decideRequest,
  isVersionManifest,
  updateAvailable,
} from "./update-policy";

/** The part of the Fetch response this needs. Keeps the module testable. */
export interface MinimalResponse {
  readonly ok: boolean;
  readonly status: number;
  json(): Promise<unknown>;
}

export interface MinimalRequestInit {
  readonly cache: "no-store";
  readonly method: "GET";
}

export type MinimalFetch = (
  url: string,
  init: MinimalRequestInit,
) => Promise<MinimalResponse>;

export type UpdateCheck =
  | { readonly status: "current"; readonly version: string }
  | { readonly status: "update-available"; readonly version: string }
  | { readonly status: "refused"; readonly reason: string }
  | { readonly status: "unavailable"; readonly reason: string };

export interface UpdateCheckOptions {
  readonly fetch: MinimalFetch;
  readonly installedVersion: string;
  readonly origin: string;
}

/**
 * Asks the server which version is published, and compares it with the one
 * running. Carries nothing but the address of the manifest.
 *
 * A failure is reported, never thrown and never swallowed: the trainee may be
 * offline, which is the normal case in the field and not an error.
 */
export async function checkForUpdate(
  options: UpdateCheckOptions,
): Promise<UpdateCheck> {
  const url = `${options.origin}${VERSION_MANIFEST_PATH}`;
  const decision = decideRequest(
    { method: "GET", url, body: null },
    options.origin,
  );
  if (decision.verdict === "blocked") {
    return { status: "refused", reason: decision.reason };
  }

  let response: MinimalResponse;
  try {
    response = await options.fetch(url, { method: "GET", cache: "no-store" });
  } catch (failure: unknown) {
    return {
      status: "unavailable",
      reason:
        failure instanceof Error
          ? failure.message
          : "The version check could not reach the server.",
    };
  }
  if (!response.ok) {
    return {
      status: "unavailable",
      reason: `The version check returned ${String(response.status)}.`,
    };
  }

  let payload: unknown;
  try {
    payload = await response.json();
  } catch {
    return { status: "unavailable", reason: "The version file is not JSON." };
  }
  if (!isVersionManifest(payload)) {
    return {
      status: "unavailable",
      reason: "The version file is not a version manifest.",
    };
  }

  return updateAvailable(options.installedVersion, payload.version)
    ? { status: "update-available", version: payload.version }
    : { status: "current", version: payload.version };
}

export function describeUpdateCheck(check: UpdateCheck): string {
  switch (check.status) {
    case "current":
      return `You are running the current build, ${check.version}.`;
    case "update-available":
      return `Build ${check.version} is available. Close every ATLas tab and reopen to take it.`;
    case "refused":
      return `The update check was refused by the application's own rules: ${check.reason}`;
    case "unavailable":
      return `No update check was possible. ${check.reason} The app keeps working offline.`;
  }
}
